import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  type?: 'website' | 'article' | 'service';
  image?: string;
  keywords?: string;
  noindex?: boolean;
  schema?: object | object[];
}

const BASE_URL = 'https://zofiadigitalagency.com';
const DEFAULT_IMAGE = `${BASE_URL}/og-image.jpg`;
const SITE_NAME = 'Zofia Digital Agency';

export const SEOHead = ({
  title,
  description,
  canonical,
  type = 'website',
  image = DEFAULT_IMAGE,
  keywords = 'marketing digitale, SEO, web development, social media, ads, PMI Italia, agenzia marketing',
  noindex = false,
  schema,
}: SEOHeadProps) => {
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const canonicalUrl = canonical ? `${BASE_URL}${canonical}` : undefined;
  
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": SITE_NAME,
    "url": BASE_URL,
    "logo": `${BASE_URL}/logo.png`,
    "description": "Agenzia di marketing digitale per PMI italiane: strategia, SEO, web development, social media, ads, automazioni AI, siti web, ecommerce",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Campobasso",
      "addressRegion": "Molise",
      "addressCountry": "IT"
    },
    "sameAs": [
      "https://www.facebook.com/profile.php?id=61582698747996",
      "https://www.instagram.com/zofia.digitalagency"
    ]
  };

  const professionalServiceSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": SITE_NAME,
    "description": "Agenzia marketing digitale per PMI B2B e B2C. Siti web, ecommerce, SEO, social media, Google Ads, automazioni AI, segretarie virtuali.",
    "areaServed": [
      { "@type": "City", "name": "Campobasso" },
      { "@type": "City", "name": "Pescara" },
      { "@type": "City", "name": "Benevento" },
      { "@type": "AdministrativeArea", "name": "Molise" },
      { "@type": "AdministrativeArea", "name": "Abruzzo" },
      { "@type": "AdministrativeArea", "name": "Campania" },
      { "@type": "AdministrativeArea", "name": "Centro Italia" },
      { "@type": "AdministrativeArea", "name": "Sud Italia" },
      { "@type": "Country", "name": "Italia" }
    ],
    "serviceType": ["Marketing Digitale", "SEO", "Web Development", "Social Media Management", "Google Ads", "Meta Ads", "Siti Vetrina", "Ecommerce", "Automazioni AI", "Segretaria Virtuale AI", "CRM"],
    "priceRange": "$$",
    "telephone": "+393294007889",
    "email": "info@zofiadigitalagency.com",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Campobasso",
      "addressRegion": "Molise",
      "addressCountry": "IT"
    }
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": SITE_NAME,
    "description": "Digital agency Campobasso, Pescara, Benevento. Marketing digitale, siti web, ecommerce, SEO, social media, automazioni AI per PMI.",
    "url": BASE_URL,
    "telephone": "+393294007889",
    "email": "info@zofiadigitalagency.com",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Campobasso",
      "addressRegion": "Molise",
      "addressCountry": "IT"
    },
    "areaServed": ["Campobasso", "Pescara", "Benevento", "Molise", "Abruzzo", "Campania", "Italia"],
    "sameAs": [
      "https://www.facebook.com/profile.php?id=61582698747996",
      "https://www.instagram.com/zofia.digitalagency"
    ]
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": SITE_NAME,
    "url": BASE_URL,
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${BASE_URL}/insights?q={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };

  const allSchemas = [
    organizationSchema,
    professionalServiceSchema,
    localBusinessSchema,
    websiteSchema,
    ...(Array.isArray(schema) ? schema : schema ? [schema] : [])
  ];

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={SITE_NAME} />
      <meta name="publisher" content={SITE_NAME} />
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      )}
      {!noindex && (
        <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      )}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:locale" content="it_IT" />
      <meta property="og:site_name" content={SITE_NAME} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      {canonicalUrl && <meta name="twitter:url" content={canonicalUrl} />}
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={fullTitle} />
      
      {/* JSON-LD Structured Data */}
      {allSchemas.map((schemaItem, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schemaItem)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEOHead;
